import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserPreference } from "@/hooks/useUserPreference";
import { useQuery } from "@tanstack/react-query";
import QuickAccess from "./QuickAccess";
import { formatDate } from "@/lib/dateUtils";
import { formatCompactTime } from "@/lib/timeUtils";
import LatestNews from "./LatestNews";
import ProjectsTable from "./ProjectsTable";
import TrainingModules from "./TrainingModules";
import QuickLinks from "./QuickLinks";
import UpcomingEvents from "./UpcomingEvents";
import UpcomingTimeOffWidget from "./UpcomingTimeOffWidget";
import TeamBirthdays from "./TeamBirthdays";
import KnowledgeHighlights from "./KnowledgeHighlights";
import WorkCapacitySection from "@/components/capacity/WorkCapacitySection";
import MilestonesWidget from "./MilestonesWidget";
import MyAllocationWidget from "./MyAllocationWidget";
import TeamCapacityWidget from "./TeamCapacityWidget";
import EffortSummaryWidget from "./EffortSummaryWidget";
import ProjectHealthWidget from "./ProjectHealthWidget";
import MyTasksSummaryWidget from "./MyTasksSummaryWidget";
import LeaveBalanceWidget from "./LeaveBalanceWidget";
import TrainingDueWidget from "./TrainingDueWidget";
import PendingApprovalsWidget from "./PendingApprovalsWidget";
import TeamAvailabilityWidget from "./TeamAvailabilityWidget";
import TeamEffortSnapshotWidget from "./TeamEffortSnapshotWidget";
import ContentPulseWidget from "./ContentPulseWidget";
import LeaveOverviewWidget from "./LeaveOverviewWidget";
import AnalyticsSnapshotWidget from "./AnalyticsSnapshotWidget";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Clock, Target, TrendingUp, Sparkles, SlidersHorizontal,
  CheckCircle, AlertTriangle, UserMinus, Users,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Task } from "@shared/schema";

import { WIDGET_REGISTRY, CATEGORY_LABELS, getGridClasses, partitionByEmptiness, DASHBOARD_DEFAULTS_VERSION, type WidgetCategory } from "@/lib/widgetRegistry";
import { DashboardTile, DashboardTileProvider } from "./DashboardTile";
import {
  isWidgetAccessible,
  getWidgetOrder,
  getDefaultHiddenWidgets,
  getStatsForRole,
} from "@/lib/dashboardConfig";

// ─── Icon map for dynamic stat rendering ─────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Clock, Target, TrendingUp, Sparkles, CheckCircle,
  AlertTriangle, UserMinus, Users,
};

// ─── Dashboard Summary Types ─────────────────────────────

interface DashboardSummary {
  upcomingMilestones: Array<{
    id: number;
    name: string;
    projectName: string;
    targetDate: string;
    status: string;
    daysUntil: number;
    isOverdue: boolean;
    isAtRisk: boolean;
  }>;
  myAllocation: {
    weekStartDate: string;
    capacity: number;
    allocatedHours: number;
    allocationPercent: number;
    projects: Array<{
      projectId: number;
      projectName: string;
      hours: number;
    }>;
  };
  effortSummary: {
    thisWeekLogged: number;
    thisWeekExpected: number;
    lastWeekLogged: number;
    variance: number;
    last7Days?: { date: string; hours: number }[];
  };
  teamCapacity: {
    thisWeek: string;
    nextWeek: string;
    overAllocated: number;
    underAllocated: number;
    optimal: number;
    totalTeamMembers: number;
  } | null;
  projectHealth: Array<{
    projectId: number;
    projectName: string;
    progress: number;
    health: "green" | "yellow" | "red";
    overdueMilestones: number;
    atRiskMilestones: number;
  }> | null;
  // New role-aware sections
  myTasksSummary?: {
    overdueCount: number;
    dueTodayCount: number;
    dueThisWeekCount: number;
    inProgressCount: number;
    totalActive: number;
  };
  leaveBalance?: Array<{
    leaveTypeId: number;
    leaveTypeName: string;
    leaveTypeColor: string;
    quota: number;
    used: number;
    remaining: number;
  }>;
  trainingDue?: Array<{
    assignmentId: number;
    moduleTitle: string;
    dueDate: string | null;
    progress: number;
    isOverdue: boolean;
  }>;
  pendingApprovals?: {
    leaveRequests: number;
    wfhRequests: number;
    total: number;
    items: Array<{
      id: number;
      type: 'leave' | 'wfh';
      requesterName: string;
      requestDate: string;
      detail: string;
    }>;
  };
  teamAvailability?: {
    inOffice: number;
    wfh: number;
    onLeave: number;
    total: number;
    onLeaveNames: string[];
    wfhNames: string[];
  };
  roleSpecificStats?: {
    orgUtilization?: number;
    teamUtilization?: number;
    pendingApprovalCount?: number;
    todayAbsences?: number;
    articlesThisWeek?: number;
    overdueTaskCount?: number;
  };
}

// ─── Widget Renderer ─────────────────────────────────────

function WidgetRenderer({
  widgetId,
  summary,
  userTasks,
}: {
  widgetId: string;
  summary?: DashboardSummary;
  userTasks: Task[];
}) {
  switch (widgetId) {
    // Personal
    case 'my_tasks_summary':
      return <MyTasksSummaryWidget tasks={userTasks} summary={summary?.myTasksSummary} />;
    case 'leave_balance':
      return <LeaveBalanceWidget balances={summary?.leaveBalance} />;
    case 'effort_summary':
      return summary?.effortSummary ? <EffortSummaryWidget effort={summary.effortSummary} /> : null;
    case 'my_allocation':
      return summary?.myAllocation ? <MyAllocationWidget allocation={summary.myAllocation} /> : null;
    case 'upcoming_time_off':
      return <UpcomingTimeOffWidget />;
    case 'training_due':
      return <TrainingDueWidget items={summary?.trainingDue} />;

    // Team
    case 'pending_approvals':
      return <PendingApprovalsWidget data={summary?.pendingApprovals} />;
    case 'team_availability':
      return <TeamAvailabilityWidget data={summary?.teamAvailability} />;
    case 'team_effort_snapshot':
      return <TeamEffortSnapshotWidget />;
    case 'team_capacity':
      return summary?.teamCapacity ? <TeamCapacityWidget capacity={summary.teamCapacity} /> : null;
    case 'work_capacity':
      return <WorkCapacitySection />;

    // Management
    case 'project_health':
      return summary?.projectHealth ? <ProjectHealthWidget projects={summary.projectHealth} /> : null;
    case 'milestones':
      return summary?.upcomingMilestones ? <MilestonesWidget milestones={summary.upcomingMilestones} /> : null;
    case 'projects_table':
      return <ProjectsTable />;

    // Content / Analytics
    case 'content_pulse':
      return <ContentPulseWidget />;
    case 'leave_overview':
      return <LeaveOverviewWidget />;
    case 'analytics_snapshot':
      return <AnalyticsSnapshotWidget />;

    // Social
    case 'latest_news':
      return <LatestNews />;
    case 'quick_links':
      return <QuickLinks />;
    case 'upcoming_events':
      return <UpcomingEvents />;
    case 'training_modules':
      return <TrainingModules />;
    case 'team_birthdays':
      return <TeamBirthdays />;
    case 'knowledge_highlights':
      return <KnowledgeHighlights />;

    default:
      return null;
  }
}

// ─── Main Component ──────────────────────────────────────

export default function DashboardWidgets() {
  const { user, hasPermission, teamMemberships } = useAuth();

  // User's team positions (deduplicated)
  const teamPositions = useMemo(() => {
    const positions = new Set(teamMemberships.map(m => m.position));
    return Array.from(positions);
  }, [teamMemberships]);

  // Widget preferences
  const [hiddenWidgets, setHiddenWidgets] = useUserPreference<string[]>("dashboard.hidden_widgets", []);
  const [widgetsInitialized, setWidgetsInitialized] = useUserPreference<boolean>("dashboard.widgets_initialized", false);
  const [defaultsVersion, setDefaultsVersion] = useUserPreference<number>("dashboard.defaults_version", 0);
  const [migrationToastShown, setMigrationToastShown] = useUserPreference<boolean>("dashboard.v2_migration_toast_shown", false);
  const { toast } = useToast();

  // First-time default initialization (no prior customization)
  useEffect(() => {
    if (widgetsInitialized || !user?.role) return;
    const defaults = getDefaultHiddenWidgets(user.role, teamPositions, hasPermission);
    setHiddenWidgets(defaults);
    setWidgetsInitialized(true);
    setDefaultsVersion(DASHBOARD_DEFAULTS_VERSION);
  }, [widgetsInitialized, user?.role, teamPositions, hasPermission]);

  // v2 defaults migration toast — for users who initialized before the redesign
  useEffect(() => {
    if (!user?.role) return;
    if (!widgetsInitialized) return;
    if (defaultsVersion >= DASHBOARD_DEFAULTS_VERSION) return;
    if (migrationToastShown) return;

    setMigrationToastShown(true);
    toast({
      title: "We've simplified the dashboard",
      description: "Reset to the new layout, or keep your current setup.",
      duration: 12000,
      action: (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            const defaults = getDefaultHiddenWidgets(user.role, teamPositions, hasPermission);
            setHiddenWidgets(defaults);
            setDefaultsVersion(DASHBOARD_DEFAULTS_VERSION);
          }}
        >
          Reset
        </Button>
      ),
    });
    // Bump version so toast doesn't reappear even if user dismisses without resetting
    setDefaultsVersion(DASHBOARD_DEFAULTS_VERSION);
  }, [widgetsInitialized, defaultsVersion, migrationToastShown, user?.role, teamPositions, hasPermission]);

  // Runtime empty-state reports from self-fetching widgets (used to demote
  // data-less tiles below populated ones).
  const [reportedEmpty, setReportedEmpty] = useState<Record<string, boolean>>({});
  const reportTileEmpty = useCallback((widgetId: string, empty: boolean) => {
    setReportedEmpty((prev) => (prev[widgetId] === empty ? prev : { ...prev, [widgetId]: empty }));
  }, []);

  // Widget visibility check
  const isWidgetVisible = (id: string) => !hiddenWidgets.includes(id);
  const toggleWidget = (id: string) => {
    setHiddenWidgets(
      hiddenWidgets.includes(id)
        ? hiddenWidgets.filter((w) => w !== id)
        : [...hiddenWidgets, id]
    );
  };

  // Ordered widget IDs based on role
  const orderedWidgetIds = useMemo(
    () => getWidgetOrder(hasPermission, user?.role, teamPositions),
    [hasPermission, user?.role, teamPositions],
  );

  // Widgets to render (accessible, visible, excluding quick_access which is rendered separately)
  const visibleWidgets = useMemo(
    () => orderedWidgetIds.filter(id =>
      id !== 'quick_access' && isWidgetVisible(id) && isWidgetAccessible(id, hasPermission, user?.role)
    ),
    [orderedWidgetIds, hiddenWidgets, hasPermission, user?.role],
  );

  // Accessible widgets for the customize sheet
  const accessibleWidgets = useMemo(
    () => WIDGET_REGISTRY.filter(w => isWidgetAccessible(w.id, hasPermission, user?.role)),
    [hasPermission, user?.role],
  );

  // Group accessible widgets by category for the sheet
  const widgetsByCategory = useMemo(() => {
    const grouped: Partial<Record<WidgetCategory, typeof accessibleWidgets>> = {};
    for (const w of accessibleWidgets) {
      if (!grouped[w.category]) grouped[w.category] = [];
      grouped[w.category]!.push(w);
    }
    return grouped;
  }, [accessibleWidgets]);

  // Data queries
  const { data: dashboardSummary } = useQuery<DashboardSummary>({
    queryKey: ["/api/dashboard/summary"],
  });

  const { data: userTasks = [] } = useQuery<Task[]>({
    queryKey: user?.id ? [`/api/tasks/assignee/${user.id}`] : [],
    enabled: !!user?.id,
  });

  // Role-adaptive stats
  const stats = useMemo(
    () => getStatsForRole(hasPermission, user?.role, teamPositions),
    [hasPermission, user?.role, teamPositions],
  );

  // Tile order: populated widgets first, data-less ones demoted to the bottom
  // so the top of the dashboard always shows useful information.
  const orderedTiles = useMemo(
    () => partitionByEmptiness(visibleWidgets, dashboardSummary, userTasks, reportedEmpty),
    [visibleWidgets, dashboardSummary, userTasks, reportedEmpty],
  );

  // Drop stale empty-reports for widgets no longer rendered (e.g. toggled off in
  // the customize sheet) so a re-shown widget isn't demoted by a stale value.
  useEffect(() => {
    setReportedEmpty((prev) => {
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const id of Object.keys(prev)) {
        if (visibleWidgets.includes(id)) next[id] = prev[id];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleWidgets]);

  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? "Good morning" : currentHour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="min-h-full">
      {/* Hero Section — Greeting + Inline Stats */}
      <div className="bg-card rounded-xl border border-border p-4 md:p-6 mb-4 md:mb-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6">
          {/* Left: Greeting */}
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" />
              <span>{formatDate(new Date())}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {greeting}, {user?.name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-muted-foreground text-sm">
              Here's what's happening at Dynamatix today
            </p>
          </div>

          {/* Right: Stats + Customize */}
          <div className="flex items-stretch gap-2 md:gap-3">
            {dashboardSummary && stats.map((stat) => {
              const IconComp = ICON_MAP[stat.icon] || Sparkles;
              const rawValue = stat.getValue(dashboardSummary);
              const displayValue = stat.key === 'hours_logged' && typeof rawValue === 'number'
                ? formatCompactTime(rawValue)
                : `${rawValue}${stat.suffix || ''}`;
              return (
                <div
                  key={stat.key}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-muted/40 border border-border min-w-[140px] flex-1 lg:flex-none"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-background shadow-sm shrink-0">
                    <IconComp className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xl md:text-2xl font-bold text-foreground leading-tight">{displayValue}</div>
                    <div className="text-xs text-muted-foreground truncate">{stat.label}</div>
                  </div>
                </div>
              );
            })}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 self-start lg:self-center" title="Customize dashboard">
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle>Customize Dashboard</SheetTitle>
                </SheetHeader>
                <div className="space-y-5 mt-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
                  <p className="text-xs text-muted-foreground">Toggle widgets on or off. Your choices are saved automatically.</p>
                  {(Object.entries(widgetsByCategory) as [WidgetCategory, typeof accessibleWidgets][]).map(([category, widgets]) => (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        {CATEGORY_LABELS[category]}
                      </h4>
                      <div className="space-y-2">
                        {widgets.map((w) => (
                          <div key={w.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                            <div className="flex-1 min-w-0 mr-3">
                              <Label htmlFor={`widget-${w.id}`} className="text-sm cursor-pointer font-medium">{w.label}</Label>
                              <p className="text-xs text-muted-foreground truncate">{w.description}</p>
                            </div>
                            <Switch
                              id={`widget-${w.id}`}
                              checked={isWidgetVisible(w.id)}
                              onCheckedChange={() => toggleWidget(w.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Quick Access Section */}
      {isWidgetVisible("quick_access") && <QuickAccess />}

      {/* Data-Driven Dashboard Grid — uniform-height tiles, empties demoted */}
      <DashboardTileProvider report={reportTileEmpty}>
        <div className="grid grid-cols-12 gap-3 md:gap-5 grid-flow-row-dense" data-tour="dashboard-grid">
          {orderedTiles.map(({ id: widgetId, empty }, index) => {
            const def = WIDGET_REGISTRY.find(w => w.id === widgetId);
            if (!def) return null;
            return (
              <div
                key={widgetId}
                className={`${getGridClasses(def)} animate-fadeIn`}
                style={{ animationDelay: `${0.1 + index * 0.05}s`, order: empty ? 1 : 0 }}
              >
                <DashboardTile
                  widgetId={widgetId}
                  label={def.label}
                  isEmpty={empty}
                  render={() => (
                    <WidgetRenderer
                      widgetId={widgetId}
                      summary={dashboardSummary}
                      userTasks={userTasks}
                    />
                  )}
                />
              </div>
            );
          })}
        </div>
      </DashboardTileProvider>
    </div>
  );
}
